import BluebirdPromise from 'bluebird';
const { Promise } = require('bluebird');
import { Time } from 'app/scripts/lib/time';
import { ninvoke } from 'app/scripts/lib/util/ninvoke';
import { QuickLoad } from '@trello/quickload';
import type { Callback, QuickloadError } from '@trello/quickload';
import { Analytics } from '@trello/atlassian-analytics';
import { ApiError } from 'app/scripts/network/api-error';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import assert from 'app/scripts/lib/assert';
import { ModelCache } from 'app/scripts/db/model-cache';
import { waitFor } from './waitFor';

const preloadUncalledSentinel = {};

const quickload = function (
  url: string,
  operationName: string,
  traceId?: string | null,
): BluebirdPromise<[JSON, XMLHttpRequest]> {
  let preload = preloadUncalledSentinel;
  return Promise.fromNode(
    (next: Callback) =>
      // @ts-expect-error
      (preload = QuickLoad.load(
        url,
        next,
        operationName,
        'model-loader',
        traceId,
        traceId ? Analytics.getTaskForTraceId(traceId) : null,
      )),
  )
    .catch(function (args: QuickloadError) {
      const [statusCode, responseText] = args;
      const apiError = ApiError.fromResponse(statusCode, responseText);
      sendNetworkErrorEvent({
        status: statusCode,
        response: apiError.toString(),
        url,
        operationName,
      });
      return Promise.reject(apiError);
    })
    .tap(() =>
      assert(
        preload !== preloadUncalledSentinel,
        "Promises resolved synchronously; didn't get preload information from QuickLoad",
      ),
    )
    .spread((data: JSON | null, xhr: XMLHttpRequest) => [data, xhr, preload]);
};

const updateServerTime = function (xhr: XMLHttpRequest) {
  let serverTime;
  // eslint-disable-next-line eqeqeq
  if ((serverTime = xhr.getResponseHeader('X-Server-Time')) != null) {
    Time.updateServerTime(parseInt(serverTime, 10));
  }
};

export const loadApiDataFromUrl = (
  url: string,
  {
    operationName,
    modelType,
    traceId,
    mappingRules,
    query,
    isHeaderLoad = false,
    metadataHeaderName,
  }: {
    operationName?: string;
    modelType: string;
    mappingRules?: object;
    query?: object;
    metadataHeaderName?: string | null;
    isHeaderLoad?: boolean | null;
    traceId?: string | null;
  },
) => {
  return quickload(url, operationName ?? `load:${modelType}`, traceId)
    .spread(function (data: JSON, xhr: XMLHttpRequest) {
      updateServerTime(xhr);

      return [data, xhr];
    })
    .tap(function () {
      // We skip this logic when we're running GraphiQL because the header is never loaded in that context.
      if (isHeaderLoad) {
        return;
      } else {
        return new BluebirdPromise(function (resolve) {
          return waitFor('headerData', resolve);
        });
      }
    })
    .spread(function (data: JSON, xhr: XMLHttpRequest) {
      return Promise.try(function () {
        if (data) {
          return ninvoke(
            ModelCache,
            'enqueueDelta',
            modelType,
            data,
            mappingRules,
            query,
          );
        } else {
          return [];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then(function (models: any) {
        if (!metadataHeaderName) {
          return models;
        }
        const metadata = JSON.parse(
          xhr.getResponseHeader(metadataHeaderName) || '{}',
        );
        return [models, metadata];
      });
    });
};
